import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const PUBLIC_TRANSPORT_GUIDE = {
  topicId: "public-transport",
  locale: "ko",
  publicationStatus: "korean_test_publish",
  risk: "R2",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "NSW Opal 네트워크·Revenue NSW fine review",
  summary:
    "NSW 대중교통을 이용할 때는 출발 전에 Trip Planner와 alerts로 경로·환승·운행변경을 확인하고, 자신의 자격에 맞는 Opal 또는 contactless 결제수단을 준비하세요. 모든 여행에서 같은 카드나 기기로 정확히 tap on·tap off하고, 카드 분실·default fare·잘못된 청구는 travel activity와 시간을 확인해 정해진 절차로 처리합니다. 유효한 ticket 없이 이동하거나 concession 증빙을 제시하지 못하면 fine이 발생할 수 있으므로 notice를 무시하지 말고, 오류나 특별한 사정이 있다면 due date 전에 Revenue NSW review 절차와 증빙요건을 확인하세요.",
  keyPoints: [
    "Trip Planner, departures와 alerts에서 실시간 경로·운행변경·예상요금을 확인하세요.",
    "Adult·Child/Youth·Concession·Gold Opal 또는 contactless 중 실제 자격과 여행에 맞는 수단을 선택하세요.",
    "한 주 동안 같은 physical card 또는 같은 digital wallet device를 사용해야 관련 fare benefits가 정확히 계산됩니다.",
    "모든 trip과 transfer에서 reader의 초록불·한 번의 소리를 확인하며 tap on·tap off하세요.",
    "등록된 Opal card는 분실 시 즉시 block하고 잔액을 보호할 수 있지만 unregistered card는 block할 수 없습니다.",
    "reader 장애·서비스 disruption으로 잘못 청구된 fare adjustment는 현재 공식 기한 안에 신청하세요.",
    "fine review는 자동 취소가 아니며 notice의 due date 전에 사실과 증빙을 제출해야 합니다.",
  ],
  sections: [
    {
      id: "plan-trip",
      title: "1. 출발 전에 경로·운행변경·접근성을 확인하세요",
      body: [
        "Transport for NSW Trip Planner에서 출발지·도착지·시간을 입력해 route suggestions, timetable, fare estimate와 transfer를 확인하세요. 바로 출발한다면 departures와 travel alerts도 확인하고, trackwork·replacement bus·platform 변경을 고려해 여유시간을 두세요.",
        "휠체어, pram, luggage, 자전거, 어린이 동반이나 assistance가 필요하면 accessible route·stop·service 정보를 별도로 확인하세요. NSW TrainLink Regional train·coach는 Opal 네트워크와 달리 좌석 booking이 필요한 경우가 있으므로 regional booking 여부를 구분하세요.",
      ],
      checklist: [
        "출발·도착 정류장, station 또는 wharf",
        "출발시간과 예상 환승시간",
        "alerts·trackwork·replacement service",
        "accessible stop·lift·boarding assistance 필요 여부",
        "Opal network인지 Regional booking 서비스인지",
        "귀가편과 지연 시 대체경로",
      ],
      sourceIds: ["public-transport-source-plan"],
    },
    {
      id: "choose-payment",
      title: "2. 자격에 맞는 결제수단을 준비하고 등록 여부를 결정하세요",
      body: [
        "Opal network에서는 Opal card, contactless credit·debit card 또는 linked device로 결제할 수 있습니다. Child/Youth, Concession, Gold Senior/Pensioner fare는 자격과 해당 카드·entitlement 연결 요건을 확인하고, 본인의 concession 증빙을 함께 준비하세요. single trip ticket은 일부 metro·train·ferry·light rail의 backup option이며 bus에는 같은 방식으로 사용할 수 없으므로 출발 전에 결제수단을 준비하세요.",
        "Opal card를 등록하면 balance·activity 확인, auto top-up, linked cards와 balance transfer, 분실 시 block 기능을 사용할 수 있습니다. contactless를 쓸 때는 physical bank card와 phone wallet이 별도 payment method로 인식될 수 있으므로 한 여행과 한 주 동안 같은 카드 또는 같은 device를 사용하세요.",
      ],
      checklist: [
        "Adult·Child/Youth·Concession·Gold 중 실제 fare 자격",
        "Opal card balance 또는 contactless 결제 가능 상태",
        "concession entitlement 연결·증빙 필요 여부",
        "Opal card 등록과 auto top-up 선택",
        "한 주 동안 사용할 동일 physical card 또는 device",
        "Regional service 별도 ticket·booking 여부",
      ],
      sourceIds: [
        "public-transport-source-fares",
        "public-transport-source-lost-card",
      ],
    },
    {
      id: "tap-and-check",
      title: "3. 매 trip에서 tap 결과와 travel activity를 확인하세요",
      body: [
        "출발할 때 tap on, 도착할 때 tap off하고 reader가 green light와 한 번의 ding을 표시하는지 확인하세요. transfer 때도 각 service의 규칙에 따라 tap하며, train에서 train으로 이어지는 동일 journey는 station 구조에 따라 중간 tap이 필요하지 않을 수 있으므로 표지와 Trip Planner 안내를 따르세요.",
        "같은 purse나 wallet 안의 여러 카드가 동시에 읽히는 card clash를 피하고, 하루·주간 cap에 도달했더라도 모든 trip에서 계속 tap하세요. tap이 빠지면 incomplete trip과 default fare가 발생하고 Opal benefits를 놓치거나 유효한 ticket 없이 이동한 것으로 처리될 수 있습니다. travel activity에서 날짜·시간·mode·charge를 정기적으로 확인하세요.",
      ],
      checklist: [
        "reader의 green light·한 번의 ding",
        "tap on과 tap off에 같은 카드·device 사용",
        "다른 contactless cards를 reader에서 분리",
        "transfer 구간별 tap 안내 확인",
        "trip activity의 출발·도착·fare",
        "unsuccessful tap이면 다른 reader·staff 도움 요청",
      ],
      sourceIds: ["public-transport-source-tapping"],
    },
    {
      id: "resolve-card-or-fare",
      title: "4. 분실·default fare·환불 문제를 정해진 절차로 처리하세요",
      body: [
        "등록된 Opal card를 잃어버리거나 도난당했다면 계정에서 즉시 block해 balance를 보호하세요. block은 되돌릴 수 없고, unregistered card는 block할 수 없습니다. 새 card를 준비한 뒤 linked card 또는 Opal Customer Care 절차로 balance transfer 가능 여부를 확인하세요.",
        "reader 장애, emergency evacuation, power·equipment outage 또는 service disruption 때문에 잘못된 fare가 부과됐다면 activity와 당시 상황을 확인해 fare adjustment를 신청하세요. 현재 공식 안내는 charge 후 90일 안에 요청하도록 하고, approved adjustment는 다음 trip에서 card에 collect해야 하므로 적용기한도 확인하세요. 더 이상 card가 필요 없거나 damaged·expired인 경우 refund form의 card number·security code·destroyed card 사진·Australian bank account 요건과 처리기간을 확인하세요.",
      ],
      checklist: [
        "Opal card 등록 여부와 card number",
        "분실·도난 시각과 즉시 block 기록",
        "travel activity·reader 위치·service disruption 증거",
        "fare adjustment 신청일과 결과",
        "replacement card·balance transfer reference",
        "refund form의 신원·card·Australian bank account 자료",
      ],
      sourceIds: [
        "public-transport-source-lost-card",
        "public-transport-source-refunds",
      ],
    },
    {
      id: "respond-to-fine",
      title: "5. 유효한 ticket을 유지하고 fine notice에는 due date 전에 대응하세요",
      body: [
        "public transport에서는 올바르게 tap한 유효한 ticket과 필요한 concession entitlement를 유지하세요. insufficient balance, missed tap 또는 잘못된 concession 사용은 fare compliance 문제로 이어질 수 있습니다. authorised staff의 요청을 받으면 침착하게 현재 payment method와 자격 증빙을 제시하고, 현장에서 다투기보다 notice·시간·장소·card activity와 상황을 기록하세요.",
        "fine이 오류라고 생각하거나 offence 당시 직접 영향을 준 특별한 사정이 있다면 Revenue NSW의 Review Assist와 evidence 요건을 확인해 due date 전에 review를 요청하세요. review는 제공한 증거를 중심으로 심사되고, 진행 중에는 fine이 hold되며 일반적으로 21일 내 답변을 목표로 하지만 복잡한 건은 더 걸릴 수 있습니다. review 결과에 동의하지 않으면 새 증거를 포함한 추가 review 또는 court election의 영향과 기한을 공식 안내에서 확인하세요.",
      ],
      sourceIds: [
        "public-transport-source-offences",
        "public-transport-source-fine-review",
      ],
    },
  ],
  faqs: [
    {
      id: "public-transport-opal-or-bank-card",
      question: "Opal card 대신 은행카드나 휴대폰으로 탈 수 있나요?",
      answer:
        "Opal network의 많은 서비스에서 contactless card·digital wallet을 사용할 수 있습니다. 다만 concession fare는 entitlement 연결 요건을 확인해야 하고, Regional train·coach는 별도 booking이 필요할 수 있습니다.",
      sourceIds: ["public-transport-source-fares"],
    },
    {
      id: "public-transport-same-card",
      question: "갈 때는 bank card, 올 때는 휴대폰 wallet을 써도 되나요?",
      answer:
        "같은 계좌라도 physical card와 digital wallet device는 다른 payment method로 인식될 수 있습니다. 정확한 fare와 weekly benefits를 위해 한 trip과 한 주 동안 같은 카드 또는 같은 device를 사용하세요.",
      sourceIds: ["public-transport-source-tapping"],
    },
    {
      id: "public-transport-forgot-tap",
      question: "tap off를 잊어서 default fare가 부과됐습니다.",
      answer:
        "activity를 확인하세요. 단순 실수인지 reader·service disruption 때문인지 구분해야 합니다. 공식 adjustment 사유에 해당한다면 현재 신청기한 안에 fare adjustment를 요청하고, 다음부터 reader의 green light와 sound를 확인하세요.",
      sourceIds: [
        "public-transport-source-tapping",
        "public-transport-source-refunds",
      ],
    },
    {
      id: "public-transport-lost-unregistered",
      question: "등록하지 않은 Opal card를 잃어버렸습니다.",
      answer:
        "Transport for NSW 안내상 unregistered Opal card는 block할 수 없습니다. 새 card를 준비하고, 남은 balance 회복 가능성은 Opal Customer Care와 현재 refund·transfer policy에서 확인하세요.",
      sourceIds: ["public-transport-source-lost-card"],
    },
    {
      id: "public-transport-lost-registered",
      question: "등록된 Opal card를 잃어버렸습니다.",
      answer:
        "Opal account에서 즉시 영구 block해 balance를 보호하세요. 새 card 종류에 맞는 replacement 절차를 진행하고 balance transfer를 요청하세요. block 후에는 찾더라도 다시 사용할 수 없습니다.",
      sourceIds: ["public-transport-source-lost-card"],
    },
    {
      id: "public-transport-refund-time",
      question: "Opal balance refund는 얼마나 걸리나요?",
      answer:
        "현재 공식 안내는 online 또는 paper refund request 처리에 최대 20 business days를 안내합니다. Australian bank account와 card 파기 사진 등 필요한 자료를 준비하고 신청 직전 최신 처리기간을 다시 확인하세요.",
      sourceIds: ["public-transport-source-refunds"],
    },
    {
      id: "public-transport-cap-still-tap",
      question: "daily 또는 weekly cap에 도달해도 계속 tap해야 하나요?",
      answer:
        "네. cap에 도달했더라도 모든 trip에서 tap on·tap off해야 유효한 ticket으로 이동하고 activity가 정확히 기록됩니다.",
      sourceIds: ["public-transport-source-tapping"],
    },
    {
      id: "public-transport-fine-review",
      question: "대중교통 fine을 받았는데 잘못 발급된 것 같습니다.",
      answer:
        "notice number, offence date, card·contactless activity, concession proof와 당시 상황 증빙을 준비해 due date 전에 Revenue NSW review를 요청하세요. review 신청만으로 취소가 보장되지는 않으며 evidence가 중요합니다.",
      sourceIds: [
        "public-transport-source-offences",
        "public-transport-source-fine-review",
      ],
    },
    {
      id: "public-transport-review-hold",
      question: "fine review 중에도 바로 납부해야 하나요?",
      answer:
        "Revenue NSW는 review가 접수되면 조사 중 fine을 hold한다고 안내합니다. 다만 접수 확인과 새 due date·결과 통지를 직접 확인하고, review 전 기존 due date를 넘기지 않도록 제때 신청하세요.",
      sourceIds: ["public-transport-source-fine-review"],
    },
  ],
  sourceIds: [
    "public-transport-source-plan",
    "public-transport-source-fares",
    "public-transport-source-tapping",
    "public-transport-source-lost-card",
    "public-transport-source-refunds",
    "public-transport-source-offences",
    "public-transport-source-fine-review",
  ],
  additionalChecks: [
    "출발 직전 Trip Planner·departures·alerts와 Regional booking 여부를 다시 확인하세요.",
    "fare, cap, concession eligibility와 minimum balance는 변경될 수 있으므로 현재 공식 페이지를 확인하세요.",
    "fare adjustment·refund·balance transfer의 신청기한과 필요자료를 제출 직전에 재확인하세요.",
    "fine notice의 offence details, due date와 review channel을 notice 원문에서 확인하세요.",
    "review 또는 court election이 향후 비용·기한에 미치는 영향은 Revenue NSW 공식 안내에서 확인하세요.",
    "접근성 assistance나 safety emergency는 일반 complaint보다 즉시 staff 또는 emergency channel을 이용하세요.",
  ],
  disclaimer:
    "이 안내는 NSW 대중교통 이용, Opal·contactless 결제와 fine 대응에 관한 일반 생활정보입니다. 특정 concession 자격, fare adjustment·refund 승인, fine 취소 또는 court 결과를 판단하지 않습니다. fare, cap, 서비스, 신청기한과 penalty는 변경될 수 있으므로 여행·신청·review 직전에 Transport for NSW와 Revenue NSW의 최신 공식 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const PUBLIC_TRANSPORT_SOURCES = [
  {
    sourceId: "public-transport-source-plan",
    topicId: "public-transport",
    title: "Plan",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/plan",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 Plan 페이지에서 Trip Planner, departures, alerts, timetables, accessible planning과 Regional bookings 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW public transport 경로·실시간 정보·운행변경·접근성·Regional booking 준비",
    followUp: "실제 여행 직전 route, platform, trackwork와 replacement service 재확인",
  },
  {
    sourceId: "public-transport-source-fares",
    topicId: "public-transport",
    title: "Fares, tickets and ways to pay",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/tickets-fares/fares",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 fares 페이지에서 Opal·contactless·single ticket, concession linkage, benefits와 Regional booking 구분 확인",
    reviewStatus: "live_verified",
    claimScope: "Opal network 결제수단, fare categories와 current benefit 구조",
    followUp: "current fare, cap, concession entitlement와 payment availability는 여행 직전 재확인",
  },
  {
    sourceId: "public-transport-source-tapping",
    topicId: "public-transport",
    title: "Tapping on and tapping off",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/tickets-fares/fares/tapping-on-tapping-off",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 tapping 페이지에서 successful tap, same payment method, transfer, default fare, cap 이후 tap과 minimum balance 확인",
    reviewStatus: "live_verified",
    claimScope: "Opal·contactless의 tap on/off 사용법과 incomplete trip·default fare 예방",
    followUp: "mode별 예외, minimum balance와 current default fare는 출발 전 재확인",
  },
  {
    sourceId: "public-transport-source-lost-card",
    topicId: "public-transport",
    title: "Lost and stolen Opal cards",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/tickets-fares/opal/lost-stolen-opal-cards",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 페이지에서 registered card 즉시 block, unregistered card 제한, replacement와 balance transfer 확인",
    reviewStatus: "live_verified",
    claimScope: "분실·도난 Opal card의 block, replacement와 balance 보호 절차",
    followUp: "card type별 replacement delivery와 balance transfer 절차는 신고 시점에 재확인",
  },
  {
    sourceId: "public-transport-source-refunds",
    topicId: "public-transport",
    title: "Opal refunds and fare adjustments",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/tickets-fares/opal/manage-your-card/opal-refunds-fare-adjustments",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 페이지에서 fare adjustment 사유·90일 기한, refund 자료·Australian bank account와 처리기간 확인",
    reviewStatus: "live_verified",
    claimScope: "Opal fare adjustment, card balance refund와 contactless reimbursement 구분",
    followUp: "제출 직전 신청기한, approved adjustment collection과 refund processing time 재확인",
  },
  {
    sourceId: "public-transport-source-offences",
    topicId: "public-transport",
    title: "Travel offences, penalties and etiquette",
    publisher: "Transport for NSW",
    url: "https://transportnsw.info/travel-info/using-public-transport/travel-offences-penalties-etiquette",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Transport for NSW 공식 페이지에서 valid ticket, fare compliance, public transport offences와 current penalty categories 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW public transport의 유효한 ticket 의무와 주요 offence·penalty notice 범위",
    followUp: "fine amount와 offence wording은 notice와 current regulation에서 재확인",
  },
  {
    sourceId: "public-transport-source-fine-review",
    topicId: "public-transport",
    title: "Request a review of your fine",
    publisher: "Revenue NSW",
    url: "https://www.nsw.gov.au/money-and-taxes/fines-and-fees/fines/request-a-review",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 5월 7일 갱신된 NSW Government 페이지에서 review 사유, ID·evidence, due date, hold·21일 일반 처리와 결과 선택지 확인",
    reviewStatus: "live_verified",
    claimScope: "Revenue NSW fine review 신청·증빙·처리와 결과 후 선택지",
    followUp: "notice 종류, due date, review channel과 court option은 실제 notice 기준으로 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === PUBLIC_TRANSPORT_GUIDE.topicId)) {
  mutableGuides.push(PUBLIC_TRANSPORT_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of PUBLIC_TRANSPORT_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
